'use client'

import { useId } from 'react'

import { useTranslation } from '@/hooks/client/use-translation'

import { PriceModelImage } from './price-model-image'
import { PriceModelSelect } from './price-model-select'
import type { PriceModelData } from './price-section-types'

interface PriceModelTabsProps {
  models: Pick<PriceModelData, 'id' | 'name' | 'fullName' | 'imageUrl'>[]
  selectedModelId: number
  onSelect: (modelId: number) => void
}

/** Image choices on white, with an image select for larger model families. */
export const PriceModelTabs = ({ models, selectedModelId, onSelect }: PriceModelTabsProps) => {
  const { t } = useTranslation()
  const groupName = useId()
  const label = t('client.product.selectModel', 'Select model')

  return (
    <div>
      {models.length >= 6 ? (
        <PriceModelSelect models={models} selectedModelId={selectedModelId} ariaLabel={label} onSelect={onSelect} />
      ) : (
        <div role="radiogroup" aria-label={label} className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
          {models.map((model) => {
            const selected = model.id === selectedModelId
            return (
              <label key={model.id} className="relative min-w-0 cursor-pointer sm:min-w-40">
                <input
                  type="radio"
                  name={groupName}
                  value={model.id}
                  aria-label={model.name}
                  checked={selected}
                  onChange={() => onSelect(model.id)}
                  data-tracking="ignore"
                  className="peer sr-only"
                />
                <span className="flex h-full min-h-16 items-center gap-2 rounded-xl border border-navy-20 bg-white p-2 pr-7 text-sm font-semibold text-navy transition-colors hover:border-lapis peer-checked:border-lapis peer-checked:ring-1 peer-checked:ring-lapis peer-focus-visible:outline-2 peer-focus-visible:outline-offset-4 peer-focus-visible:outline-lapis">
                  <PriceModelImage imageUrl={model.imageUrl} modelName={model.fullName || model.name} />
                  <span className="min-w-0 break-words">{model.name}</span>
                </span>
                {selected && (
                  <svg
                    aria-hidden="true"
                    viewBox="0 0 16 16"
                    fill="none"
                    className="absolute top-2.5 right-2 size-3.5 text-lapis"
                  >
                    <path d="m3 8.25 3.1 3.1L13 4.75" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                )}
              </label>
            )
          })}
        </div>
      )}
    </div>
  )
}
